import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Building2, User, Mail, Lock, Eye, EyeOff, ArrowRight, ShieldCheck, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button, Input } from '../components/ui';

const Signup = () => {
  const [businessName, setBusinessName] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { setSession } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await axios.post('/api/signup', { businessName, name, email, password });
      // Immediate access, matching the 14-day-trial-no-card decision — no
      // email verification gate on first login.
      setSession(res.data.token, res.data.user);
      navigate('/');
    } catch (err) {
      const backendMsg = err.response?.data?.msg;
      const fieldErrors = err.response?.data?.errors;
      if (fieldErrors?.length) {
        setError(fieldErrors.join(' '));
      } else if (backendMsg) {
        setError(backendMsg);
      } else if (err.request) {
        setError('Cannot reach server. Please try again shortly.');
      } else {
        setError('Signup failed: ' + err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-neutral-50 to-neutral-100 p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-9 h-9 bg-primary-600 rounded-lg flex items-center justify-center shadow-sm">
            <Building2 size={18} className="text-white" />
          </div>
          <span className="text-lg font-semibold tracking-tight text-neutral-900">NexifyPOS</span>
        </div>

        <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-[0_18px_50px_-20px_rgba(15,23,42,0.28)] p-8 sm:p-9">
          <div className="mb-7">
            <div className="w-12 h-12 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center mb-4 ring-1 ring-primary-100">
              <Building2 size={20} />
            </div>
            <h2 className="text-2xl font-bold text-neutral-900 tracking-tight">Start your free trial</h2>
            <p className="text-sm text-neutral-500 mt-1.5">14 days free, no card required.</p>
          </div>

          {error && (
            <div className="mb-5 p-3 rounded-lg bg-danger-50 border border-danger-100 text-danger-700 text-sm flex items-start gap-2">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              label="Business name"
              leftIcon={<Building2 size={16} />}
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder="Your Restaurant Name"
              autoComplete="organization"
              required
            />
            <Input
              label="Your name"
              leftIcon={<User size={16} />}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Doe"
              autoComplete="name"
              required
            />
            <Input
              label="Email address"
              type="email"
              leftIcon={<Mail size={16} />}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@restaurant.com"
              autoComplete="email"
              required
            />
            <Input
              label="Password"
              type={showPassword ? 'text' : 'password'}
              leftIcon={<Lock size={16} />}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="text-neutral-400 hover:text-neutral-700 transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              }
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              autoComplete="new-password"
              required
            />
            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={submitting}
              rightIcon={!submitting && <ArrowRight size={18} />}
            >
              {submitting ? 'Creating your workspace…' : 'Start free trial'}
            </Button>
          </form>

          <div className="mt-7 pt-5 border-t border-neutral-100 flex items-center justify-center gap-1.5 text-xs text-neutral-400">
            <ShieldCheck size={13} className="text-success-500" />
            Secured with 256-bit encryption
          </div>
        </div>

        <p className="text-sm text-neutral-500 text-center mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-primary-600 font-medium hover:text-primary-700">Sign in</Link>
        </p>
      </div>
    </div>
  );
};

export default Signup;
